import { formatCredits, isValidScale, MONEY_EQUIVALENT_LABEL } from '@/ops/credits';
import { formatMinor } from '@/ops/format';

interface CreditAmountProps {
  /** Integer micro-credits, as the server sent them. */
  micro: number | null | undefined;
  /** `credit_display_scale` from the same answer (or billing settings). Unknown → raw micro-credits. */
  scale: number | null | undefined;
  /** The money equivalent the SERVER computed (`price_minor`) — never derived here (BL6). */
  moneyMinor?: number | null;
  currency?: string | null;
  /** One line instead of two, for dense table cells. */
  inline?: boolean;
}

/**
 * Credits first, money second (D-CP21): the displayed credits (micro ÷ scale), and under them the money
 * equivalent labelled "at current pack price" when billing supplied one. Nothing here is stored.
 */
export function CreditAmount({ micro, scale, moneyMinor, currency, inline = false }: CreditAmountProps) {
  const credits = formatCredits(micro, scale);
  const hasMoney = moneyMinor !== null && moneyMinor !== undefined && !!currency;
  const money = hasMoney ? `${formatMinor(moneyMinor, currency)} ${MONEY_EQUIVALENT_LABEL}` : null;
  const scaleUnknown = micro !== null && micro !== undefined && !isValidScale(scale);

  if (inline) {
    return (
      <span className="credit-amount" title={scaleUnknown ? 'Display scale unavailable — raw micro-credits.' : undefined}>
        <span className="tabular">{credits}</span>
        {money && <span className="t-caption muted"> · {money}</span>}
      </span>
    );
  }

  return (
    <span className="credit-amount credit-amount-block">
      <span className="tabular">{credits}</span>
      {money && <span className="t-caption muted">{money}</span>}
      {scaleUnknown && <span className="t-caption subtle">display scale unavailable — raw micro-credits</span>}
    </span>
  );
}
